import React, {
    ButtonHTMLAttributes,
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import { BRANDING_UPDATED_EVENT, loadAffiliateBrandingFromStorage } from 'helpers/co2helpers/affiliate_branding_helper';
import { adjustColor, hexToRgba, isColorDark } from 'utils/color';

type ButtonVariant = 'primary' | 'secondary' | 'alternate1' | 'alternate2' | 'danger';
type Appearance = 'light' | 'dark';

const DARK_ALPHA = 0.25;
const HOVER_SHIFT = 0.2;
const BORDER_ALPHA = 0.1;
const BORDER_ALPHA_NORMAL = 0.6;

type PaletteColor = {
    background: string;
    text: string;
    darkBackground?: string;
    darkText?: string;
    hover?: string;
    darkHover?: string;
    border?: string;
    darkBorder?: string;
    icon?: string;
    darkIcon?: string;
};

type Co2UniversalButtonContextValue = {
    palette: Record<ButtonVariant, PaletteColor>;
    resolvedVariant: ButtonVariant;
};

const Co2UniversalButtonContext = createContext<Co2UniversalButtonContextValue | null>(null);

export const useCo2UniversalButtonContext = () => useContext(Co2UniversalButtonContext);

/** Use inside Co2UniversalButton label to tint text with the resolved (branding-aware) palette. */
export const Co2UniversalButtonAccent = ({
    children,
    variant,
    style,
}: {
    children: ReactNode;
    variant?: ButtonVariant;
    style?: React.CSSProperties;
}) => {
    const ctx = useCo2UniversalButtonContext();
    if (!ctx) return <span style={style}>{children}</span>;
    const targetVariant = variant ?? ctx.resolvedVariant;
    const accentColor = ctx.palette[targetVariant].background;
    return (
        <span
            style={{
                color: accentColor,
                fontWeight: 700,
                ...style,
            }}
        >
            {children}
        </span>
    );
};

export type Co2ButtonProps = {
    variant?: ButtonVariant;
    iconClass?: string;
    iconPosition?: 'left' | 'right';
    loading?: boolean;
    onIconClick?: () => void;
    themeOverride?: Appearance;
    paletteOverride?: Partial<Record<ButtonVariant, PaletteColor>>;
    useBrandingPalette?: boolean;
    iconRounded?: boolean;
    iconColour?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const DEFAULT_PALETTE: Record<ButtonVariant, PaletteColor> = {
    primary: { background: '#0AB39C', darkBackground: '#0AB39C', hover: '#099885', darkHover: '#177C70', border: '#31D4BF', darkBorder: '#189C8D', text: '#ffffff', darkText: '#ffffff', icon: '#ffffff', darkIcon: '#ffffff' },
    secondary: { background: '#F5F5F5', darkBackground: '#1C4992', hover: '#e8e8e8', darkHover: '#173d7b', border: '#EDEAEA', darkBorder: '#1C4D9B', text: '#000000', darkText: '#ffffff', icon: '#000000', darkIcon: '#ffffff' },
    alternate1: { background: '#50c3e6', darkBackground: '#50C3E6', hover: '#35b8df', darkHover: '#1b8fac', border: '#71CAE6', darkBorder: '#197C95', text: '#FAFAFA', darkText: '#ffffff', icon: '#FFFFFF', darkIcon: '#ffffff' },
    alternate2: { background: '#89A3E0', darkBackground: '#89A3E0', hover: '#7B9BE6', darkHover: '#7B9BE6', border: '#9BB2E8', darkBorder: '#9BB2E8', text: '#FCFDFE', darkText: '#ffffff', icon: '#FFFFFF', darkIcon: '#ffffff' },
    danger: { background: '#F06548', darkBackground: '#F06548', hover: '#D9573C', darkHover: '#C04A32', border: '#F4846E', darkBorder: '#C04A32', text: '#ffffff', darkText: '#ffffff', icon: '#ffffff', darkIcon: '#ffffff' },
};

const getThemeAppearance = (): Appearance => {
    const attr =
        typeof document !== 'undefined'
            ? document.documentElement.getAttribute('data-bs-theme')
            : undefined;

    if (attr === 'dark') return 'dark';
    if (attr === 'light') return 'light';

    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
    }

    return 'light';
};

const useThemeAppearance = (override?: Appearance) => {
    const [appearance, setAppearance] = useState<Appearance>(() =>
        override ?? getThemeAppearance()
    );

    useEffect(() => {
        if (override) {
            setAppearance(override);
            return;
        }
        const update = () => setAppearance(getThemeAppearance());

        let mq: MediaQueryList | undefined;
        if (typeof window !== 'undefined' && window.matchMedia) {
            mq = window.matchMedia('(prefers-color-scheme: dark)');
            try {
                mq.addEventListener?.('change', update);
            } catch {
                mq?.addListener?.(update);
            }
        }

        let observer: MutationObserver | undefined;
        if (typeof document !== 'undefined') {
            observer = new MutationObserver(update);
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-bs-theme'],
            });
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('app:themechange', update);
        }

        return () => {
            if (mq) {
                try {
                    mq.removeEventListener?.('change', update);
                } catch {
                    mq?.removeListener?.(update);
                }
            }
            observer?.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('app:themechange', update);
            }
        };
    }, []);

    return appearance;
};

const Co2UniversalButton = ({
    variant,
    children,
    iconClass,
    iconPosition,
    loading,
    disabled,
    style,
    onClick,
    onIconClick,
    themeOverride,
    paletteOverride,
    useBrandingPalette = true,
    iconRounded,
    iconColour,
    type: buttonType,
    ...rest
}: Co2ButtonProps) => {
    const brandingButtonsFromRedux = useSelector(
        (state: any) => state?.Co2Profile?.userAffiliate?.branding_buttons
    );
    const [brandingButtonsFromStorage, setBrandingButtonsFromStorage] = useState(() => {
        const stored = loadAffiliateBrandingFromStorage();
        return stored?.branding_buttons ?? null;
    });

    const brandingButtons = useMemo(() => {
        if (brandingButtonsFromRedux) return brandingButtonsFromRedux;
        return brandingButtonsFromStorage;
    }, [brandingButtonsFromRedux, brandingButtonsFromStorage]);

    useEffect(() => {
        const handleBrandingUpdate = () => {
            const stored = loadAffiliateBrandingFromStorage();
            setBrandingButtonsFromStorage(stored?.branding_buttons ?? null);
        };
        window.addEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
        return () => window.removeEventListener(BRANDING_UPDATED_EVENT, handleBrandingUpdate);
    }, []);

    const fromSettings = useMemo(() => {
        if (!useBrandingPalette) return null;
        if (!brandingButtons) return null;
        try {
            const raw =
                typeof brandingButtons === 'string'
                    ? JSON.parse(brandingButtons)
                    : brandingButtons;
            const toPalette = (
                bgKey: string,
                textKey: string,
                darkKey: string,
                hoverKey: string,
                darkHoverKey: string,
                borderKey: string,
                darkBorderKey: string,
                iconKey: string,
                darkIconKey: string,
                darkTextKey: string
            ): PaletteColor | null => {
                const bg = raw?.[bgKey];
                const text = raw?.[textKey];
                if (!bg || !text) return null;
                const dark = raw?.[darkKey];
                const hover = raw?.[hoverKey];
                const darkHover = raw?.[darkHoverKey];
                const border = raw?.[borderKey];
                const darkBorder = raw?.[darkBorderKey];
                const icon = raw?.[iconKey];
                const darkIcon = raw?.[darkIconKey];
                const darkText = raw?.[darkTextKey];
                return {
                    background: String(bg),
                    text: String(text),
                    ...(dark ? { darkBackground: String(dark) } : {}),
                    ...(hover ? { hover: String(hover) } : {}),
                    ...(darkHover ? { darkHover: String(darkHover) } : {}),
                    ...(border ? { border: String(border) } : {}),
                    ...(darkBorder ? { darkBorder: String(darkBorder) } : {}),
                    ...(icon ? { icon: String(icon) } : {}),
                    ...(darkIcon ? { darkIcon: String(darkIcon) } : {}),
                    ...(darkText ? { darkText: String(darkText) } : {}),
                };
            };
            const primary = toPalette('primary_bg', 'primary_text', 'primary_bg_dark', 'primary_hover', 'primary_hover_dark', 'primary_border', 'primary_border_dark', 'primary_icon', 'primary_icon_dark', 'primary_text_dark');
            const secondary = toPalette('secondary_bg', 'secondary_text', 'secondary_bg_dark', 'secondary_hover', 'secondary_hover_dark', 'secondary_border', 'secondary_border_dark', 'secondary_icon', 'secondary_icon_dark', 'secondary_text_dark');
            const alternate1 = toPalette('alternate1_bg', 'alternate1_text', 'alternate1_bg_dark', 'alternate1_hover', 'alternate1_hover_dark', 'alternate1_border', 'alternate1_border_dark', 'alternate1_icon', 'alternate1_icon_dark', 'alternate1_text_dark');
            const alternate2 = toPalette('alternate2_bg', 'alternate2_text', 'alternate2_bg_dark', 'alternate2_hover', 'alternate2_hover_dark', 'alternate2_border', 'alternate2_border_dark', 'alternate2_icon', 'alternate2_icon_dark', 'alternate2_text_dark');
            return {
                ...(primary ? { primary } : {}),
                ...(secondary ? { secondary } : {}),
                ...(alternate1 ? { alternate1 } : {}),
                ...(alternate2 ? { alternate2 } : {}),
            } as Partial<Record<ButtonVariant, PaletteColor>>;
        } catch {
            return null;
        }
    }, [brandingButtons, useBrandingPalette]);

    const resolvedVariant: ButtonVariant = variant || 'secondary';
    const resolvedAppearance = useThemeAppearance(themeOverride);
    const resolvedIconPosition = iconPosition || 'left';
    const content = children;
    const hasLabel = React.Children.toArray(children).length > 0;

    const palette = useMemo(
        () => ({
            ...DEFAULT_PALETTE,
            ...(fromSettings || {}),
            ...(paletteOverride || {}),
        }),
        [fromSettings, paletteOverride]
    );
    const baseBackgroundLight = palette[resolvedVariant].background;
    const baseBackgroundDark =
        palette[resolvedVariant].darkBackground ?? palette[resolvedVariant].background;
    const baseText = palette[resolvedVariant].text;
    const baseTextDark = palette[resolvedVariant].darkText ?? baseText;
    const baseHoverLight = palette[resolvedVariant].hover;
    const baseHoverDark = palette[resolvedVariant].darkHover ?? baseHoverLight;
    const baseIconLight = palette[resolvedVariant].icon ?? baseText;
    const baseIconDark = palette[resolvedVariant].darkIcon ?? baseTextDark;
    const baseBorderLight = palette[resolvedVariant].border;
    const baseBorderDark = palette[resolvedVariant].darkBorder ?? baseBorderLight;
    const darkMode = resolvedAppearance === 'dark';

    const hoverBackground = useMemo(() => {
        const explicitHover = darkMode ? baseHoverDark : baseHoverLight;
        if (explicitHover) return explicitHover;
        const base = darkMode ? baseBackgroundDark : baseBackgroundLight;
        const shouldLighten = isColorDark(base);
        const adjusted = adjustColor(base, HOVER_SHIFT, shouldLighten);
        return darkMode ? hexToRgba(adjusted, DARK_ALPHA) : adjusted;
    }, [baseBackgroundDark, baseBackgroundLight, baseHoverDark, baseHoverLight, darkMode]);

    const standardBorderColor = useMemo(() => {
        const explicitBorder = darkMode ? baseBorderDark : baseBorderLight;
        if (explicitBorder) return explicitBorder;
        const base = darkMode ? baseBackgroundDark : baseBackgroundLight;
        const shouldLighten = isColorDark(base);
        const adjusted = adjustColor(base, BORDER_ALPHA, shouldLighten);
        return darkMode ? hexToRgba(adjusted, BORDER_ALPHA_NORMAL) : adjusted;
    }, [baseBackgroundDark, baseBackgroundLight, baseBorderDark, baseBorderLight, darkMode]);

    const [hovered, setHovered] = useState(false);

    const resolvedBackground = darkMode
        ? hexToRgba(baseBackgroundDark, DARK_ALPHA)
        : baseBackgroundLight;
    const resolvedTextColor = darkMode ? baseTextDark : baseText;
    const resolvedIconColor = iconColour ?? (darkMode ? baseIconDark : baseIconLight);
    const backgroundColor = hovered ? hoverBackground : resolvedBackground;
    const resolvedBorderColor =
        (darkMode ? baseBorderDark : baseBorderLight) ||
        hexToRgba(darkMode ? baseBackgroundDark : baseBackgroundLight, BORDER_ALPHA);
    const borderColor = hovered ? resolvedBorderColor : standardBorderColor;
    const dividerColor = borderColor;//hovered ? borderColor : hoverBackground;

    const buttonStyles: React.CSSProperties = iconRounded
        ? {
            background: 'none',
            backgroundColor: 'transparent',
            border: 'none',
            color: resolvedIconColor,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 5,
            padding: 0,
            height: 34,
            fontWeight: 300,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'opacity 150ms ease',
            gap: 2,
            ...style,
        }
        : {
            background: 'none',
            backgroundColor,
            border: `1px solid ${borderColor}`,
            color: resolvedTextColor,
            display: 'inline-flex',
            alignItems: 'stretch',
            borderRadius: 5,
            padding: 0,
            height: 34,
            fontWeight: 300,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition:
                'background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
            ...style,
        };

    // Ring visible on tinted fills; derived `standardBorderColor` is usually too close to icon bg hue.
    const iconRoundedRingColor =
        (darkMode ? baseBorderDark : baseBorderLight) ||
        (darkMode ? 'rgba(255, 255, 255, 0.24)' : 'rgba(33, 37, 41, 0.14)');

    const iconBoxStyle: React.CSSProperties = iconRounded
        ? {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            boxSizing: 'border-box',
            border: `1px solid ${iconRoundedRingColor}`,
            backgroundColor: hovered ? hoverBackground : (darkMode ? hexToRgba(baseBackgroundDark, DARK_ALPHA) : baseBackgroundLight),
            color: resolvedIconColor,
            lineHeight: 0,
            flexShrink: 0,
            transition: 'background-color 150ms ease, border-color 150ms ease',
        }
        : {
            display: 'grid',
            placeItems: 'center',
            padding: '8px 8px',
            borderRight:
                hasLabel && resolvedIconPosition === 'left'
                    ? `1px solid ${dividerColor}`
                    : undefined,
            borderLeft:
                hasLabel && resolvedIconPosition === 'right'
                    ? `1px solid ${dividerColor}`
                    : undefined,
            color: resolvedIconColor,
            lineHeight: 0,
        };

    const iconRoundedLabelColor = darkMode ? baseTextDark : '#333333';

    const labelStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        padding: iconRounded
            ? '0 0 0 2px'
            : hasLabel
                ? resolvedIconPosition === 'left'
                    ? '0 16px 0 14px'
                    : '0 14px 0 16px'
                : '0 8px',
        color: iconRounded ? iconRoundedLabelColor : resolvedTextColor,
        gap: 4,
        whiteSpace: 'nowrap',
    };

    const renderIcon = () => {
        if (!iconClass) return null;
        const node = (
            <i
                className={iconClass}
                aria-hidden
                style={{ color: 'inherit', fontSize: 12, lineHeight: 1 }}
            />
        );

        const handleIconClick = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.stopPropagation();
            onIconClick ? onIconClick() : onClick?.(event as React.MouseEvent<HTMLButtonElement>);
        };

        return (
            <span
                style={iconBoxStyle}
                onClick={onIconClick || onClick ? handleIconClick : undefined}
            >
                {node}
            </span>
        );
    };

    const shouldRenderLabel = hasLabel || loading;

    return (
        <Co2UniversalButtonContext.Provider value={{ palette, resolvedVariant }}>
            <button
                type={buttonType ?? 'button'}
                style={buttonStyles}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={onClick}
                disabled={disabled || loading}
                {...rest}
            >
                {resolvedIconPosition === 'left' && renderIcon()}
                {shouldRenderLabel && (
                    <span style={labelStyle}>
                        {iconRounded && hasLabel ? (
                            <span style={{ marginRight: 8 }}>{content}</span>
                        ) : (
                            content
                        )}
                        {loading && (
                            <Spinner
                                size="sm"
                                className="flex-shrink-0"
                                color={darkMode ? 'light' : undefined}
                            />
                        )}
                    </span>
                )}
                {resolvedIconPosition === 'right' && renderIcon()}
            </button>
        </Co2UniversalButtonContext.Provider>
    );
};

export default Co2UniversalButton;
